import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export default function ContactPage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'general',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const channels = [
    {
      titleKey: 'pages.contact.generalInquiries',
      title: 'General Inquiries',
      email: 'hello@zhisu.trade',
      desc: 'For general questions about ZhiSu',
      icon: 'ri-mail-line',
    },
    {
      titleKey: 'pages.contact.support',
      title: 'Developer Support',
      email: 'dev@zhisu.trade',
      desc: 'API integration, SDK questions, and technical support',
      icon: 'ri-code-box-line',
    },
    {
      titleKey: 'pages.contact.press',
      title: 'Legal & Compliance',
      email: 'legal@zhisu.trade',
      desc: 'Regulatory inquiries, legal matters, and compliance issues',
      icon: 'ri-scales-line',
    },
    {
      titleKey: 'pages.contact.partnerships',
      title: 'Business Development',
      email: 'partners@zhisu.trade',
      desc: 'Partnerships, integrations, and institutional inquiries',
      icon: 'ri-service-line',
    },
    {
      title: 'Bug Bounty',
      email: 'security@zhisu.trade',
      desc: 'Report security vulnerabilities and participate in our bounty program',
      icon: 'ri-bug-line',
    },
    {
      title: 'Careers',
      email: 'careers@zhisu.trade',
      desc: 'Join our team and build the future of DeFi',
      icon: 'ri-briefcase-line',
    },
  ];

  const offices = [
    {
      city: 'Singapore',
      address: 'ZhiSu Pte. Ltd.\n1 Raffles Place\n#28-00 Tower 2\nSingapore 048616',
    },
    {
      city: 'Dubai',
      address: 'ZhiSu DMCC\nUnit 1807, Jumeirah Business Center 3\nCluster Y, Jumeirah Lake Towers\nDubai, UAE',
    },
    {
      city: 'British Virgin Islands',
      address: 'ZhiSu Inc.\nP.O. Box 3174\nRoad Town, Tortola\nBritish Virgin Islands',
    },
  ];

  return (
    <CompanyLayout
      title={t('contact.title', 'Contact')}
      subtitle={t('contact.subtitle', 'We are here to help. Reach out through any of the channels below.')}
    >
      {/* Contact Channels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {channels.map((ch) => (
          <a
            key={ch.title}
            href={`mailto:${ch.email}`}
            className={`p-5 rounded-xl border ${borderLight} ${bgCard} hover:border-[#00ff88]/20 transition-all group`}
          >
            <div className="w-10 h-10 rounded-lg bg-[#00ff88]/10 flex items-center justify-center mb-3 group-hover:bg-[#00ff88]/20 transition-colors">
              <i className={`${ch.icon} text-[#00ff88] w-5 h-5 flex items-center justify-center`} />
            </div>
            <h3 className={`font-semibold text-sm mb-1 ${textPrimary}`}>{ch.titleKey ? t(ch.titleKey, ch.title) : ch.title}</h3>
            <p className={`text-xs mb-2 ${textMuted}`}>{ch.desc}</p>
            <span className="text-sm text-[#00ff88] font-mono">{ch.email}</span>
          </a>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Contact Form */}
        <div className={`flex-1 p-6 md:p-8 rounded-xl border ${borderLight} ${bgCard}`}>
          <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>{t('contact.sendMessage', 'Send a Message')}</h2>
          {submitted ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[#00ff88]/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-[#00ff88] w-8 h-8 flex items-center justify-center" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${textPrimary}`}>Message Sent!</h3>
              <p className={`text-sm ${textSecondary}`}>
                {t('contact.response', 'We typically respond within 24 hours.')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>{t('contact.name', 'Name')}</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-3 py-2.5 rounded-lg border ${borderLight} bg-transparent text-sm ${textPrimary} focus:outline-none focus:border-[#00ff88]/40 transition-colors`}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>{t('contact.email', 'Email')}</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-3 py-2.5 rounded-lg border ${borderLight} bg-transparent text-sm ${textPrimary} focus:outline-none focus:border-[#00ff88]/40 transition-colors`}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>{t('contact.subject', 'Category')}</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className={`w-full px-3 py-2.5 rounded-lg border ${borderLight} bg-transparent text-sm ${textPrimary} focus:outline-none focus:border-[#00ff88]/40 transition-colors`}
                >
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Support</option>
                  <option value="partnership">Partnership</option>
                  <option value="media">Press & Media</option>
                  <option value="legal">Legal</option>
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>{t('contact.message', 'Message')}</label>
                <textarea
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className={`w-full px-3 py-2.5 rounded-lg border ${borderLight} bg-transparent text-sm ${textPrimary} focus:outline-none focus:border-[#00ff88]/40 transition-colors resize-none`}
                  placeholder="How can we help you?"
                />
                <p className={`text-xs mt-1 text-right ${textMuted}`}>
                  {formData.message.length}/500
                </p>
              </div>
              <button
                type="submit"
                className="w-full py-2.5 rounded-lg bg-[#00ff88]/10 text-[#00ff88] text-sm font-semibold hover:bg-[#00ff88]/20 transition-colors"
              >
                {t('contact.sendMessage', 'Send Message')}
              </button>
            </form>
          )}
        </div>

        {/* Offices */}
        <div className="lg:w-80 flex-shrink-0 space-y-4">
          {offices.map((office) => (
            <div key={office.city} className={`p-5 rounded-xl border ${borderLight} ${bgCard}`}>
              <h3 className={`font-semibold text-sm mb-2 ${textPrimary}`}>{office.city}</h3>
              <p className={`text-sm whitespace-pre-line leading-relaxed ${textSecondary}`}>
                {office.address}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Social */}
      <div className={`mt-8 p-6 rounded-xl border ${borderLight} ${bgCard}`}>
        <h2 className={`text-lg font-bold mb-4 ${textPrimary}`}>{t('contact.followUs', 'Community & Social')}</h2>
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'X / Twitter', icon: 'ri-twitter-x-line', href: '#' },
            { label: 'Discord', icon: 'ri-discord-line', href: '#' },
            { label: 'Telegram', icon: 'ri-telegram-line', href: '#' },
            { label: 'GitHub', icon: 'ri-github-line', href: '#' },
            { label: 'LinkedIn', icon: 'ri-linkedin-line', href: '#' },
            { label: 'YouTube', icon: 'ri-youtube-line', href: '#' },
          ].map((social) => (
            <a
              key={social.label}
              href={social.href}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${borderLight} text-sm ${textSecondary} hover:text-[#00ff88] hover:border-[#00ff88]/20 transition-all`}
            >
              <i className={`${social.icon} w-4 h-4 flex items-center justify-center`} />
              {social.label}
            </a>
          ))}
        </div>
      </div>
    </CompanyLayout>
  );
}